// FE-derived per-line variance indicators for Line Item Review. Display-only
// (D5 — no persisted columns): each recomputes from the raw line fields already
// in the payload, so editing a value updates the indicators with no round-trip.
// Formulas are the code-reconciled definitions (D7); pct(x) = x * 100. Each value
// is a signed percentage, or null when undefined (rendered as '--'). These sit
// alongside the boolean decimalVarianceFlagged the read-side already surfaces.

import { format3 } from 'utils/number';

export interface VarianceInputs {
  lineItemDecimal: number | null;
  price: number | null;
  grossVolume: number | null;
  grossValue: number | null;
  grossTaxes: number | null;
  grossAdjustments: number | null;
  grossNet: number | null;
  ownerVolume: number | null;
  ownerValue: number | null;
  lineItemNetValue: number | null;
}

export interface LineItemVariance {
  ownerNetValuePercent: number | null;
  decCalcValuePercent: number | null;
  decCalcVolumePercent: number | null;
  grossValuePercent: number | null;
  grossNetPercent: number | null;
}

const num = (value: number | null | undefined): number | null => {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

const pct = (x: number): number => x * 100;

// A ratio, or null when the divisor is missing or zero (guards divide-by-zero).
const ratio = (
  numerator: number | null,
  denom: number | null
): number | null => {
  if (numerator === null || denom === null || denom === 0) return null;
  return numerator / denom;
};

// A "share of a base" percent (value − calc) / base, or null when the base is
// missing/zero.
const sharePct = (
  value: number | null,
  calc: number | null,
  base: number | null
): number | null => {
  if (value === null || calc === null || base === null || base === 0) {
    return null;
  }
  return pct((value - calc) / base);
};

export const computeLineItemVariance = (
  row: VarianceInputs
): LineItemVariance => {
  const decimal = num(row.lineItemDecimal);
  const grossValue = num(row.grossValue);
  const grossVolume = num(row.grossVolume);
  const grossNet = num(row.grossNet);
  const grossTaxes = num(row.grossTaxes);
  const grossAdjustments = num(row.grossAdjustments);
  const ownerValue = num(row.ownerValue);
  const ownerVolume = num(row.ownerVolume);
  const price = num(row.price);
  const statedOwnerNet = num(row.lineItemNetValue);

  // #2 Owner Net Value Variance: stated Line Item Net Value (line_Item_Net_Val__c)
  // vs decimal × gross_net (D7) — NOT the pre-tax "Owner Value" column.
  const calcOwnerNet =
    decimal !== null && grossNet !== null ? decimal * grossNet : null;
  const ownerNetValuePercent = sharePct(
    statedOwnerNet,
    calcOwnerNet,
    statedOwnerNet
  );

  // #3 Dec Calc (Value): owner_value / gross_value − decimal.
  const valueImplied = ratio(ownerValue, grossValue);
  const decCalcValuePercent =
    valueImplied === null || decimal === null
      ? null
      : pct(valueImplied - decimal);

  // #4 Dec Calc (Volume): owner_volume / gross_volume − decimal. '--' for
  // tax/deduction lines (gross volume blank) — confirmed Gross VOLUME, not Value.
  const volumeImplied = ratio(ownerVolume, grossVolume);
  const decCalcVolumePercent =
    volumeImplied === null || decimal === null
      ? null
      : pct(volumeImplied - decimal);

  // #5 Gross Value: gross_value vs price × gross_volume (D7).
  const calcGrossValue =
    price !== null && grossVolume !== null ? price * grossVolume : null;
  const grossValuePercent = sharePct(grossValue, calcGrossValue, grossValue);

  // #6 Gross Net: gross_net vs gross_value + gross_taxes + gross_adjustment (signed).
  // Only meaningful when gross tax/adjustment is itemized; when neither is (both
  // 0/null — some sources report tax only at owner level) render '--', not a false 0.
  const grossTaxesItemized =
    (grossTaxes ?? 0) !== 0 || (grossAdjustments ?? 0) !== 0;
  const calcGrossNet =
    grossValue !== null && grossTaxes !== null && grossAdjustments !== null
      ? grossValue + grossTaxes + grossAdjustments
      : null;
  const grossNetPercent = grossTaxesItemized
    ? sharePct(grossNet, calcGrossNet, grossNet)
    : null;

  return {
    ownerNetValuePercent,
    decCalcValuePercent,
    decCalcVolumePercent,
    grossValuePercent,
    grossNetPercent,
  };
};

// '12.340%' | '-4.560%' | '0.000%' | '--'. Rounded to 3 decimals;
// internally-consistent lines render as '0.000%'.
export const formatVariancePct = (value: number | null): string => {
  return value === null ? '--' : `${format3(value)}%`;
};
